import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/routes";
import { useAuth } from "@/hooks/use-auth";
import type {
  Empresa,
  UserProfile,
  Laudo,
  Peca,
  Lote,
  InviteCode,
  UserInvite,
  Pergunta,
  BancoPergunta,
  Fatura,
  Lancamento,
  FinanceiroStats,
  Veiculo,
  VeiculoComLaudo,
  LaudoWithDetails,
  ConfiguracoesVistoriadora,
  Notificacao,
} from "@shared/schema";

export function useAuthFetch() {
  const { session } = useAuth();
  return async (url: string) => {
    const headers: Record<string, string> = {};
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }
    const res = await fetch(url, { headers, cache: "no-store" });
    if (!res.ok) throw new Error(`Erro: ${res.status} ${res.statusText}`);
    return res.json();
  };
}

export function useEmpresas() {
  const authFetch = useAuthFetch();
  return useQuery<Empresa[]>({
    queryKey: [api.empresas.list.path],
    queryFn: () => authFetch(api.empresas.list.path),
  });
}

export type ResumoCreditosParceiro = { parceiroId: string; disponivel: number; reservado: number; debitado: number };

// Créditos por Parceiro (aba Empresas): só Owner e Admin/RT de Vistoriadora leem.
export function useResumoCreditosParceiros(enabled: boolean) {
  const authFetch = useAuthFetch();
  return useQuery<ResumoCreditosParceiro[]>({
    queryKey: ["/api/creditos/resumo-parceiros"],
    queryFn: () => authFetch("/api/creditos/resumo-parceiros"),
    enabled,
  });
}

export type VersaoAppUsuario = { userId: string; versao: string; vistoEm: string };

// Última versão do APP vista no login por documento (só Owner).
export function useVersoesApp(enabled: boolean) {
  const authFetch = useAuthFetch();
  return useQuery<VersaoAppUsuario[]>({
    queryKey: ["/api/owner/app-versoes"],
    queryFn: () => authFetch("/api/owner/app-versoes"),
    enabled,
  });
}

export function useEmpresa(id?: string) {
  const authFetch = useAuthFetch();
  return useQuery<Empresa>({
    queryKey: [api.empresas.get.path, id],
    queryFn: () => authFetch(`/api/empresas/${id}`),
    enabled: !!id,
  });
}

export function useParceirosByVistoriadora(vistoriadoraId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<Empresa[]>({
    queryKey: [api.empresas.parceiros.path, vistoriadoraId],
    queryFn: () => authFetch(`/api/empresas/${vistoriadoraId}/parceiros`),
    enabled: !!vistoriadoraId,
  });
}

export function useUsuarios(
  empresaId?: string,
  enabled = true,
  vistoriadoraId?: string,
) {
  const authFetch = useAuthFetch();
  return useQuery<UserProfile[]>({
    queryKey: [api.usuarios.list.path, empresaId, vistoriadoraId],
    queryFn: () => {
      let url: string = api.usuarios.list.path;
      if (vistoriadoraId) {
        url = `${api.usuarios.list.path}?vistoriadoraId=${vistoriadoraId}`;
      } else if (empresaId) {
        url = `${api.usuarios.list.path}?empresaId=${empresaId}`;
      }
      return authFetch(url);
    },
    enabled,
  });
}

export function useLaudos(
  vistoriadoraId?: string,
  status?: string,
  mes?: number,
  ano?: number,
  parceiroId?: string,
) {
  const authFetch = useAuthFetch();
  return useQuery<LaudoWithDetails[]>({
    queryKey: [api.laudos.list.path, vistoriadoraId, parceiroId, status, mes, ano],
    queryFn: () => {
      const params = new URLSearchParams();
      if (vistoriadoraId) params.set("vistoriadoraId", vistoriadoraId);
      if (parceiroId) params.set("parceiroId", parceiroId);
      if (status) params.set("status", status);
      if (mes) params.set("mes", String(mes));
      if (ano) params.set("ano", String(ano));
      return authFetch(`${api.laudos.list.path}?${params}`);
    },
  });
}

export function useAllLaudos(status?: string, mes?: number, ano?: number) {
  const authFetch = useAuthFetch();
  return useQuery<LaudoWithDetails[]>({
    queryKey: [api.laudos.list.path, status, mes, ano],
    queryFn: () => {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (mes) params.set("mes", String(mes));
      if (ano) params.set("ano", String(ano));
      return authFetch(`${api.laudos.list.path}?${params}`);
    },
  });
}

export function useLaudo(laudoId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<LaudoWithDetails>({
    queryKey: [api.laudos.get.path, laudoId],
    queryFn: () => authFetch(`/api/laudos/${laudoId}`),
    enabled: !!laudoId,
  });
}

export function useLaudoPdfStatus(laudoId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<{ bloqueado: boolean; motivo?: string }>({
    queryKey: ["laudo-pdf-status", laudoId],
    queryFn: () => authFetch(`/api/laudos/${laudoId}/pdf-status`),
    enabled: !!laudoId,
  });
}

export function useLaudoStats(vistoriadoraId?: string, parceiroId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<Record<string, number>>({
    queryKey: [api.laudos.stats.path, vistoriadoraId, parceiroId],
    queryFn: () => {
      const params = new URLSearchParams();
      if (vistoriadoraId) params.set("vistoriadoraId", vistoriadoraId);
      if (parceiroId) params.set("parceiroId", parceiroId);
      const qs = params.toString();
      return authFetch(`${api.laudos.stats.path}${qs ? `?${qs}` : ""}`);
    },
  });
}

export function usePecas(tipoVeiculo?: string) {
  const authFetch = useAuthFetch();
  return useQuery<Peca[]>({
    queryKey: [api.pecas.list.path, tipoVeiculo],
    queryFn: () => {
      const params = tipoVeiculo ? `?tipoVeiculo=${tipoVeiculo}` : "";
      return authFetch(`${api.pecas.list.path}${params}`);
    },
  });
}

export type LoteWithVistoriadora = Lote & {
  vistoriadoraNome: string;
  valorEstimado: number | null;
  parceiroNome: string | null;
};

export function useLote(id?: string) {
  const authFetch = useAuthFetch();
  return useQuery<LoteWithVistoriadora>({
    queryKey: [api.lotes.get.path, id],
    queryFn: () => authFetch(api.lotes.get.path.replace(":id", id!)),
    enabled: !!id,
  });
}

export function useLotes(vistoriadoraId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<LoteWithVistoriadora[]>({
    queryKey: [api.lotes.list.path, vistoriadoraId],
    queryFn: () => {
      const params = vistoriadoraId ? `?vistoriadoraId=${vistoriadoraId}` : "";
      return authFetch(`${api.lotes.list.path}${params}`);
    },
  });
}

export function usePecaPerguntas(nome?: string, tipoVeiculo?: string) {
  const authFetch = useAuthFetch();
  return useQuery<Pergunta[]>({
    queryKey: [api.pecaPerguntas.list.path, nome, tipoVeiculo],
    queryFn: () =>
      authFetch(
        `${api.pecaPerguntas.list.path}?nome=${encodeURIComponent(nome!)}&tipoVeiculo=${tipoVeiculo}`,
      ),
    enabled: !!nome && !!tipoVeiculo,
  });
}

export function usePerguntasPadrao(tipoVeiculo?: string) {
  const authFetch = useAuthFetch();
  return useQuery<Pergunta[]>({
    queryKey: [api.perguntasPadrao.list.path, tipoVeiculo],
    queryFn: () =>
      authFetch(`${api.perguntasPadrao.list.path}?tipoVeiculo=${tipoVeiculo}`),
    enabled: !!tipoVeiculo,
  });
}

export function useInviteCodes(vistoriadoraId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<InviteCode[]>({
    queryKey: [api.inviteCodes.list.path, vistoriadoraId],
    queryFn: () => {
      const params = vistoriadoraId ? `?vistoriadoraId=${vistoriadoraId}` : "";
      return authFetch(`${api.inviteCodes.list.path}${params}`);
    },
  });
}

export function useUserInvites() {
  const authFetch = useAuthFetch();
  return useQuery<UserInvite[]>({
    queryKey: [api.userInvites.list.path],
    queryFn: () => authFetch(api.userInvites.list.path),
  });
}

export function useFinanceiroStats(vistoriadoraId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<FinanceiroStats>({
    queryKey: [api.financeiro.stats.path, vistoriadoraId],
    queryFn: () => {
      const params = vistoriadoraId ? `?vistoriadoraId=${vistoriadoraId}` : "";
      return authFetch(`${api.financeiro.stats.path}${params}`);
    },
  });
}

export function useFaturas(
  vistoriadoraId?: string,
  mes?: number,
  ano?: number,
  status?: string,
  loteId?: string,
) {
  const authFetch = useAuthFetch();
  return useQuery<Fatura[]>({
    queryKey: [
      api.financeiro.faturas.list.path,
      vistoriadoraId,
      mes,
      ano,
      status,
      loteId,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (vistoriadoraId) params.set("vistoriadoraId", vistoriadoraId);
      if (mes) params.set("mes", String(mes));
      if (ano) params.set("ano", String(ano));
      if (status) params.set("status", status);
      if (loteId) params.set("loteId", loteId);
      return authFetch(`${api.financeiro.faturas.list.path}?${params}`);
    },
  });
}

export function useBancoPerguntas() {
  const authFetch = useAuthFetch();
  return useQuery<BancoPergunta[]>({
    queryKey: [api.bancoPerguntas.list.path],
    queryFn: () => authFetch(api.bancoPerguntas.list.path),
  });
}

export function useVeiculos(parceiroId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<VeiculoComLaudo[]>({
    queryKey: [api.veiculos.list.path, parceiroId],
    queryFn: () => {
      const params = parceiroId ? `?parceiroId=${parceiroId}` : "";
      return authFetch(`${api.veiculos.list.path}${params}`);
    },
  });
}

export function useParceirosParaVeiculos(enabled = true) {
  const authFetch = useAuthFetch();
  return useQuery<Empresa[]>({
    queryKey: [api.veiculos.parceiros.path],
    queryFn: () => authFetch(api.veiculos.parceiros.path),
    enabled,
  });
}

export function useLancamentos(vistoriadoraId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<Lancamento[]>({
    queryKey: [api.financeiro.lancamentos.list.path, vistoriadoraId],
    queryFn: () => {
      const params = vistoriadoraId ? `?vistoriadoraId=${vistoriadoraId}` : "";
      return authFetch(`${api.financeiro.lancamentos.list.path}${params}`);
    },
  });
}

export interface AuditoriaParams {
  entidade?: string;
  acao?: string;
  empresaId?: string;
  dataInicio?: string;
  dataFim?: string;
  page?: number;
}

export interface AuditoriaLog {
  id: string;
  user_id: string;
  user_nome: string;
  empresa_id: string;
  empresa_nome: string;
  entidade: string;
  entidade_id: string | null;
  entidade_descricao: string | null;
  acao: string;
  detalhes: Record<string, any> | null;
  criado_em: string;
}

export interface AuditoriaResult {
  data: AuditoriaLog[];
  total: number;
  page: number;
  limit: number;
}

export function useConfiguracoes() {
  const authFetch = useAuthFetch();
  return useQuery<ConfiguracoesVistoriadora | null>({
    queryKey: [api.configuracoes.get.path],
    queryFn: () => authFetch(api.configuracoes.get.path),
  });
}

export function useAuditoria(params: AuditoriaParams) {
  const authFetch = useAuthFetch();
  return useQuery<AuditoriaResult>({
    queryKey: [api.auditoria.list.path, params],
    queryFn: () => {
      const p = new URLSearchParams();
      if (params.entidade) p.set("entidade", params.entidade);
      if (params.acao) p.set("acao", params.acao);
      if (params.empresaId) p.set("empresaId", params.empresaId);
      if (params.dataInicio) p.set("dataInicio", params.dataInicio);
      if (params.dataFim) p.set("dataFim", params.dataFim);
      if (params.page && params.page > 1) p.set("page", String(params.page));
      const qs = p.toString();
      return authFetch(`${api.auditoria.list.path}${qs ? `?${qs}` : ""}`);
    },
  });
}

export function useNotificacoes() {
  const authFetch = useAuthFetch();
  return useQuery<Notificacao[]>({
    queryKey: [api.notificacoes.list.path],
    queryFn: () => authFetch(api.notificacoes.list.path),
    refetchInterval: 30_000,
  });
}

export function useMarcarNotificacaoLida() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch(
        api.notificacoes.marcarLida.path.replace(":id", id),
        {
          method: "PATCH",
          headers,
        },
      );
      if (!res.ok) throw new Error("Erro ao marcar notificação");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.notificacoes.list.path] });
    },
  });
}

export function useMarcarTodasNotificacoesLidas() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch(api.notificacoes.marcarTodasLidas.path, {
        method: "POST",
        headers,
      });
      if (!res.ok) throw new Error("Erro ao marcar todas como lidas");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.notificacoes.list.path] });
    },
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// CRÉDITOS
// ──────────────────────────────────────────────────────────────────────────────

import { supabase } from "@/lib/supabase";
import { apiRequest } from "@/lib/queryClient";

export interface CreditoSaldo {
  tipo_veiculo: string;
  disponivel: number;
  reservado: number;
  devendo?: number;
}

export interface CreditoTransacao {
  id: string;
  tipo:
    | "compra"
    | "reserva"
    | "debito"
    | "liberacao"
    | "estorno_credito"
    | "estorno_dinheiro";
  quantidade: number;
  criado_em: string;
  compra_id?: string;
  laudo_id?: string;
  tipo_veiculo: string;
}

export interface CompraItem {
  tipo_veiculo: string;
  quantidade: number;
  preco_unitario: number;
  subtotal: number;
}

export interface CompraCredito {
  id: string;
  user_id: string;
  empresa_id: string;
  valor_total: number;
  metodo_pagamento: string;
  status: string;
  asaas_payment_id?: string | null;
  criado_em: string;
  paid_at?: string | null;
  itens: CompraItem[];
  // Computed helpers (mapped from real columns for compatibility)
  quantidade: number;
  tipo_veiculo: string;
  data: string;
  metodo: string;
}


// Saldo utilizável (lotes do ambiente atual): creditos_balance é agregado e mistura crédito de teste e de Produção.
export function useCreditosSaldo(empresaId?: string) {
  const authFetch = useAuthFetch();
  return useQuery<CreditoSaldo[]>({
    queryKey: ["creditos_balance", empresaId],
    queryFn: async () => {
      if (!empresaId) return [];
      return (await authFetch(`/api/creditos/saldo-utilizavel?parceiroId=${empresaId}`)) ?? [];
    },
    enabled: !!empresaId,
  });
}

export function useCreditosTransacoes(empresaId?: string) {
  return useQuery<CreditoTransacao[]>({
    queryKey: ["credito_transacoes", empresaId],
    queryFn: async () => {
      if (!empresaId) return [];
      const { data, error } = await supabase
        .from("credito_transacoes")
        .select(
          "id, tipo, quantidade, criado_em, compra_id, laudo_id, tipo_veiculo, descricao",
        )
        .eq("empresa_id", empresaId)
        .order("criado_em", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data || [];
    },
    enabled: !!empresaId,
  });
}

// Busca transações por empresa_id (sem filtro user_id) para chips nos cards de laudo.
// Necessário porque debito transactions têm user_id = RT/TV (quem finalizou),
// não o parceiro logado. Filtra apenas transações vinculadas a laudos.
export function useCreditosTransacoesPorEmpresa(empresaId?: string) {
  return useQuery<CreditoTransacao[]>({
    queryKey: ["credito_transacoes_empresa", empresaId],
    queryFn: async () => {
      if (!empresaId) return [];
      const { data, error } = await supabase
        .from("credito_transacoes")
        .select("id, tipo, quantidade, criado_em, laudo_id, tipo_veiculo")
        .eq("empresa_id", empresaId)
        .not("laudo_id", "is", null)
        .in("tipo", ["reserva", "debito", "gratuito"]);
      if (error) throw error;
      return data || [];
    },
    enabled: !!empresaId,
  });
}

export function useCompra(compraId?: string) {
  const { user } = useAuth();
  return useQuery<CompraCredito>({
    queryKey: ["compra_credito", compraId],
    queryFn: async () => {
      if (!user?.id || !compraId) throw new Error("Missing user or compra ID");
      const { data, error } = await supabase
        .from("compras_credito")
        .select(
          "id, user_id, empresa_id, itens, valor_total, metodo_pagamento, status, asaas_payment_id, criado_em, paid_at",
        )
        .eq("id", compraId)
        .eq("user_id", user.id)
        .single();
      if (error) throw error;
      const raw = data as any;
      const quantidade = ((raw.itens as CompraItem[]) || []).reduce(
        (s, i) => s + i.quantidade,
        0,
      );
      return {
        ...raw,
        quantidade,
        tipo_veiculo: raw.itens?.[0]?.tipo_veiculo ?? "",
        data: raw.criado_em,
        metodo: raw.metodo_pagamento,
      } as CompraCredito;
    },
    enabled: !!user?.id && !!compraId,
  });
}

interface CompraComParceiro extends CompraCredito {
  user_nome: string;
  empresa_nome: string;
}

export function useComprasHistorico(empresaId?: string) {
  const { user } = useAuth();
  return useQuery<CompraComParceiro[]>({
    queryKey: ["compras_historico", empresaId],
    queryFn: async () => {
      if (!user?.id || !empresaId) return [];

      // Step 1: Buscar todos os parceiros que pertencem a esta vistoriadora
      const { data: parceiros, error: parceirosError } = await supabase
        .from("empresas")
        .select("id")
        .eq("vistoriadora_id", empresaId);

      if (parceirosError) throw parceirosError;

      const parceiroIds = (parceiros || []).map((p: any) => p.id);
      if (parceiroIds.length === 0) return [];

      // Step 2: Buscar compras dos parceiros desta vistoriadora
      const { data, error } = await supabase
        .from("compras_credito")
        .select(
          `id, user_id, empresa_id, itens, valor_total, metodo_pagamento, status,
           asaas_payment_id, criado_em, paid_at,
           user_profiles(nome),
           empresas!empresa_id(razao_social, nome_fantasia)`,
        )
        .in("empresa_id", parceiroIds)
        .order("criado_em", { ascending: false });
      if (error) throw error;

      return (data || []).map((compra: any) => {
        const quantidade = ((compra.itens as CompraItem[]) || []).reduce(
          (s: number, i: CompraItem) => s + i.quantidade,
          0,
        );
        return {
          ...compra,
          quantidade,
          tipo_veiculo: compra.itens?.[0]?.tipo_veiculo ?? "",
          data: compra.criado_em,
          metodo: compra.metodo_pagamento,
          user_nome: compra.user_profiles?.nome || "Desconhecido",
          empresa_nome:
            compra.empresas?.nome_fantasia ||
            compra.empresas?.razao_social ||
            "Desconhecida",
        };
      }) as CompraComParceiro[];
    },
    enabled: !!user?.id && !!empresaId,
  });
}

interface CompraComParceiroEVistoriadora extends CompraComParceiro {
  vistoriadora_id: string;
  vistoriadora_nome: string;
}

export function useComprasHistoricoOwner() {
  const authFetch = useAuthFetch();
  const { user } = useAuth();
  return useQuery<CompraComParceiroEVistoriadora[]>({
    queryKey: ["compras_historico_owner"],
    queryFn: () => authFetch(api.vendas.historicoOwner.path),
    enabled: !!user?.id,
  });
}
